"use client";

import * as React from "react";
import Link from "next/link";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogTrigger,
} from "@/components/dialog/dialog/dialog";
import { Button } from "@/components/button/button/button";
import type { NormalizedCardButton } from "./buttonVisibility";
import styles from "./cardOverlay.module.css";

export interface CardOverlayProps {
  /**
   * Buttons to render inside the overlay. Already split out from the
   * card's "on card" buttons via splitButtons().
   */
  buttons: NormalizedCardButton[];
  /** Optional heading inside the overlay (typically the card's title). */
  title?: string;
  /** Optional supporting copy below the title. */
  description?: string;
  /**
   * Card surface that opens the overlay when clicked. Slotted in as the
   * Radix DialogTrigger child so all a11y wiring (role, tabindex, keys)
   * happens automatically.
   */
  trigger: React.ReactNode;
  /** Controlled open state (optional — defaults to uncontrolled). */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

const CardOverlay = ({
  buttons,
  title,
  description,
  trigger,
  open,
  onOpenChange,
}: CardOverlayProps) => {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent size="sm">
        <div className={styles.body}>
          {title ? (
            <DialogTitle className={styles.title}>{title}</DialogTitle>
          ) : null}
          {description ? (
            <p className={styles.description}>{description}</p>
          ) : null}
          <div className={styles.buttons}>
            {buttons.map((btn, idx) => {
              const label = btn.copy || btn.title || "";
              const hasHref = !!btn.href;
              const target = btn.isBlank ? "_blank" : undefined;
              const rel = btn.isBlank ? "noopener noreferrer" : undefined;
              return (
                <Button
                  key={btn.id ?? idx}
                  variant={btn.variant}
                  size={btn.size}
                  asChild={hasHref}
                  className={styles.button}
                >
                  {hasHref ? (
                    <Link
                      href={btn.href}
                      target={target}
                      rel={rel}
                      title={btn.title}
                    >
                      {label}
                    </Link>
                  ) : (
                    <span title={btn.title}>{label}</span>
                  )}
                </Button>
              );
            })}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default CardOverlay;
